import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import {
  CreateItAssetDocumentManuallyDocument,
  ItAssetDocumentsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'

interface Props {
  assetId: string
  onClose: () => void
}

const UploadAssetDocumentModal = ({ assetId, onClose }: Props) => {
  const [files, setFiles] = React.useState<File[]>([])
  const [isUploading, setIsUploading] = React.useState(false)

  const [createItAssetDocument] = useMutation(CreateItAssetDocumentManuallyDocument, {
    extractorFn: (data) => data.itManagementMutations.createManuallyItmanagementItAssetDocument,
  })

  const handleFilesAdd = (newFiles: File[]) => {
    setFiles([...files, ...newFiles])
  }

  const handleFileDelete = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (files.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.asset_details.documents.no_files_selected'),
      })
      return
    }

    setIsUploading(true)

    try {
      for (const file of files) {
        const result = await createItAssetDocument({
          variables: {
            itAssetId: assetId,
            document: file,
          },
          refetchQueries: [
            {
              query: ItAssetDocumentsDocument,
              variables: { itAssetId: assetId },
            },
          ],
        })

        const handled = handleResult(result, () => undefined)
        if (!handled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.documents_upload_failed'),
          })
          return
        }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.documents_upload_failed'),
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <OneModal isOpen onClose={onClose}>
      <OneModal.Header title={i18n.t('it_management.asset_details.documents.upload_document')} />
      <OneModal.Content withPadding>
        <Box gap="s16" flexDirection="column">
          <Text size="200" weight="normal" color="grey700">
            {i18n.t('it_management.asset_details.documents.upload_document_helper_text')}
          </Text>
          <DropzoneBox
            onDrop={handleFilesAdd}
            message={i18n.t('documents.drop_here')}
            accept={acceptedContentTypes()}
            iconName="common-file-text-upload"
          />
          <FileList files={files} onDeleteFile={handleFileDelete} spaced={false} />
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box gap="s8" flexDirection="row" justifyContent="flexEnd" width="full">
          <F0Button
            variant="outline"
            onClick={onClose}
            label={i18n.t('common.cancel')}
            disabled={isUploading}
          />
          <F0Button
            onClick={handleSubmit}
            loading={isUploading}
            label={isUploading ? i18n.t('common.saving') : i18n.t('common.save')}
            disabled={files.length === 0 || isUploading}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default UploadAssetDocumentModal
